import React, { useState, useRef, useContext, useEffect } from 'react'
import Navbar from '../components/Navbar'
import InnerLeadBanner from '../components/InnerLeadBanner'
import { useNavigate } from 'react-router-dom';
import JoditEditor from 'jodit-react'
import { useApi } from '../context/ApiContext';
import AuthContext from '../context/AuthContext'

const PostOpportunity = () => {
    let { user } = useContext(AuthContext)
    const API_BASE_URL = useApi();
    const navigate = useNavigate();
    const [postingType, setPostingType] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [opportunityDetails, setOpportunityDetails] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const [showPaymentRetry, setShowPaymentRetry] = useState(false);
    const [formData, setFormData] = useState({
        opportunityType: '',
        title: '',
        firmOrPersonal: '',
        firm: '',
        remuneration: '',
        modality: '',
        location: '',
        company_phone: '',
        application_mode: '',
        your_email: user ? user.email : '',
        applicantDetails: [],
        icon: null,
        opportunity_details: '',
        is_featured: false,
        // New fields for different application modes
        whatsapp_number: '',
        external_link: '',
        application_email: '',
        phone_number: ''
    });

    const editor = useRef(null);

    // Load Paystack script
    useEffect(() => {
        const script = document.createElement('script');
        script.src = "https://js.paystack.co/v1/inline.js";
        script.async = true;
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        }
    }, []);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImage(file);
            
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
            
            setFormData({
                ...formData,
                icon: file
            });
        }
    };

    const removeImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
        setFormData({
            ...formData,
            icon: null
        });
    };

    const handleDetailsChange = (content) => {
        setOpportunityDetails(content);
        setFormData({
            ...formData,
            opportunity_details: content
        });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (type === 'checkbox') {
            if (name === 'is_featured') {
                setFormData({
                    ...formData,
                    [name]: checked
                });
            } else {
                const updatedDetails = checked
                    ? [...formData.applicantDetails, value]
                    : formData.applicantDetails.filter(detail => detail !== value);
                
                setFormData({
                    ...formData,
                    [name]: updatedDetails
                });
            }
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
            
            if (name === 'firmOrPersonal') {
                setPostingType(value);
            }
        }
    };

    // Function to submit form data to server
    const submitOpportunity = async (isFeatured = false, paymentReference = null) => {
        const submitData = new FormData();
        
        // Append all form fields to FormData
        Object.keys(formData).forEach(key => {
            if (key === 'applicantDetails') {
                submitData.append(key, JSON.stringify(formData[key]));
            } else if (key === 'icon' && formData[key]) {
                submitData.append(key, formData[key]);
            } else if (key === 'is_featured') {
                submitData.append(key, isFeatured);
            } else {
                submitData.append(key, formData[key]);
            }
        });
        
        // Add payment reference if available
        if (paymentReference) {
            submitData.append('payment_reference', paymentReference);
        }
        
        try {
            let tokens = localStorage.getItem("authTokens");
            let accessToken = tokens ? JSON.parse(tokens).access : null;
            let fetchUrl = `${API_BASE_URL}/post-opportunity`;
            const response = await fetch(fetchUrl, {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + accessToken,
                },
                body: submitData,
            });
            
            if (response.ok) {
                const result = await response.json();
                console.log('Opportunity posted successfully:', result);
                
                if (isFeatured) {
                    alert('Payment successful! Your opportunity has been submitted for review and will be featured for 24 hours.');
                } else {
                    alert('Opportunity submitted successfully for review!');
                }
                
                navigate('/');
            } else {
                console.error('Failed to post opportunity:', response.statusText);
                alert('Failed to post opportunity. Please try again.');
            }
        } catch (error) {
            console.error('Error posting opportunity:', error);
            alert('An error occurred. Please try again.');
        } finally {
            setIsSubmitting(false);
            setPaymentProcessing(false);
            setShowPaymentRetry(false);
        }
    };

    // Function to initialize Paystack payment
    const initializePayment = () => {
        const amount = 2000; // 20 GHS in kobo
        const email = formData.your_email || user.email;
        const firstName = user ? user.first_name || '' : '';
        const lastName = user ? user.last_name || '' : '';

        // Generate a unique reference
        const reference = `JOBFLOW-${Date.now()}`;

        if (window.PaystackPop) {
            const handler = window.PaystackPop.setup({
                key: 'pk_live_4c3dcc596f216f140a292649e96167da919c9590', // Replace with your Paystack public key
                email: email,
                amount: amount,
                currency: 'GHS',
                ref: reference,
                metadata: {
                    custom_fields: [
                        {
                            display_name: "First Name",
                            variable_name: "first_name",
                            value: firstName
                        },
                        {
                            display_name: "Last Name",
                            variable_name: "last_name",
                            value: lastName
                        }
                    ]
                },
                callback: function(response) {
                    // Payment successful
                    if (response.status === 'success') {
                        submitOpportunity(true, response.reference);
                    } else {
                        // Payment failed, show retry option
                        setShowPaymentRetry(true);
                        setPaymentProcessing(false);
                    }
                },
                onClose: function() {
                    // User closed the payment window
                    setShowPaymentRetry(true);
                    setPaymentProcessing(false);
                }
            });
            handler.openIframe();
        } else {
            alert('Payment processor not loaded. Please try again.');
            setPaymentProcessing(false);
            setShowPaymentRetry(true);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        // Validate form data based on application mode
        if (!formData.title || !formData.opportunityType || !formData.location || !formData.your_email) {
            alert('Please fill in all required fields.');
            setIsSubmitting(false);
            return;
        }
        
        // Validate application mode specific fields
        if (formData.application_mode === 'whatsapp' && !formData.whatsapp_number) {
            alert('Please provide a WhatsApp number for applications.');
            setIsSubmitting(false);
            return;
        }
        
        if (formData.application_mode === 'external' && !formData.external_link) {
            alert('Please provide an external link for applications.');
            setIsSubmitting(false);
            return;
        }
        
        if (formData.application_mode === 'email' && !formData.application_email) {
            alert('Please provide an email address for applications.');
            setIsSubmitting(false);
            return;
        }
        
        if (formData.application_mode === 'phone_call' && !formData.phone_number) {
            alert('Please provide a phone number for applications.');
            setIsSubmitting(false);
            return;
        }
        
        // If user selected featured option, process payment
        if (formData.is_featured) {
            setPaymentProcessing(true);
            initializePayment();
        } else {
            // Regular submission without payment
            submitOpportunity(false);
        }
    };

    const handleRetryPayment = () => {
        setShowPaymentRetry(false);
        setPaymentProcessing(true);
        initializePayment();
    };

    const handlePostAsNormal = () => {
        setShowPaymentRetry(false);
        submitOpportunity(false);
    };

    // Render application form based on selected mode
    const renderApplicationForm = () => {
        switch (formData.application_mode) {
            case 'jobflow':
                return (
                    <div className="col-sm-12">
                        <div className="form-group">
                            <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                                What applicant details should be collected? <span style={{ whiteSpace: 'nowrap' }}>(Check all that apply)</span>
                            </label>
                            
                            <div style={{ border: '1px solid #ddd', borderRadius: '5px', padding: '15px' }}>
                                <div className="form-check" style={{ marginBottom: '12px' }}>
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        id="requireFullName"
                                        name="applicantDetails"
                                        value="fullName"
                                        checked={formData.applicantDetails.includes('fullName')}
                                        onChange={handleInputChange}
                                    />
                                    <label className="form-check-label" htmlFor="requireFullName">
                                        Applicant full name
                                    </label>
                                </div>
                                
                                <div className="form-check" style={{ marginBottom: '12px' }}>
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        id="requireDOB"
                                        name="applicantDetails"
                                        value="dateOfBirth"
                                        checked={formData.applicantDetails.includes('dateOfBirth')}
                                        onChange={handleInputChange}
                                    />
                                    <label className="form-check-label" htmlFor="requireDOB">
                                        Applicant date of birth
                                    </label>
                                </div>
                                
                                <div className="form-check" style={{ marginBottom: '12px' }}>
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        id="requirePhone"
                                        name="applicantDetails"
                                        value="phoneNumber"
                                        checked={formData.applicantDetails.includes('phoneNumber')}
                                        onChange={handleInputChange}
                                    />
                                    <label className="form-check-label" htmlFor="requirePhone">
                                        Applicant phone number
                                    </label>
                                </div>
                                
                                <div className="form-check" style={{ marginBottom: '12px' }}>
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        id="requireEmail"
                                        name="applicantDetails"
                                        value="email"
                                        checked={formData.applicantDetails.includes('email')}
                                        onChange={handleInputChange}
                                    />
                                    <label className="form-check-label" htmlFor="requireEmail">
                                        Applicant email
                                    </label>
                                </div>
                                
                                <div className="form-check" style={{ marginBottom: '12px' }}>
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        id="requireCV"
                                        name="applicantDetails"
                                        value="cv"
                                        checked={formData.applicantDetails.includes('cv')}
                                        onChange={handleInputChange}
                                    />
                                    <label className="form-check-label" htmlFor="requireCV">
                                        Applicant CV
                                    </label>
                                </div>
                                
                                <div className="form-check" style={{ marginBottom: '12px' }}>
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        id="districtLocation"
                                        name="applicantDetails"
                                        value="districtLocation"
                                        checked={formData.applicantDetails.includes('districtLocation')}
                                        onChange={handleInputChange}
                                    />
                                    <label className="form-check-label" htmlFor="districtLocation">
                                        District & Location
                                    </label>
                                </div>
                            </div>
                            
                            <small className="form-text text-muted" style={{ marginTop: '8px' }}>
                                Check all details you want to require from applicants
                            </small>
                        </div>
                    </div>
                );
            
            case 'whatsapp':
                return (
                    <div className="col-sm-12">
                        <div className="form-group">
                            <label htmlFor="whatsapp_number" style={{ fontWeight: '500' }}>
                                WhatsApp Number for Applications *
                            </label>
                            <input 
                                className="form-control valid" 
                                name="whatsapp_number" 
                                id="whatsapp_number" 
                                type="tel" 
                                value={formData.whatsapp_number}
                                onChange={handleInputChange}
                                placeholder="e.g., +233 55 123 4567" 
                                required
                            />
                            <small className="form-text text-muted">
                                Applicants will contact this WhatsApp number directly
                            </small>
                        </div>
                    </div>
                );
            
            case 'external':
                return (
                    <div className="col-sm-12">
                        <div className="form-group">
                            <label htmlFor="external_link" style={{ fontWeight: '500' }}>
                                External Application Link *
                            </label>
                            <input 
                                className="form-control valid" 
                                name="external_link" 
                                id="external_link" 
                                type="url" 
                                value={formData.external_link}
                                onChange={handleInputChange}
                                placeholder="https://example.com/apply" 
                                required
                            />
                            <small className="form-text text-muted">
                                Applicants will be redirected to this external link to apply
                            </small>
                        </div>
                    </div>
                );
            
            case 'email':
                return (
                    <div className="col-sm-12">
                        <div className="form-group">
                            <label htmlFor="application_email" style={{ fontWeight: '500' }}>
                                Email Address for Applications *
                            </label>
                            <input 
                                className="form-control valid" 
                                name="application_email" 
                                id="application_email" 
                                type="email" 
                                value={formData.application_email}
                                onChange={handleInputChange}
                                placeholder="applications@company.com" 
                                required
                            />
                            <small className="form-text text-muted">
                                Applicants will send their applications to this email address
                            </small>
                        </div>
                    </div>
                );
            
            case 'phone_call':
                return (
                    <div className="col-sm-12">
                        <div className="form-group">
                            <label htmlFor="phone_number" style={{ fontWeight: '500' }}>
                                Phone Number for Applications *
                            </label>
                            <input 
                                className="form-control valid" 
                                name="phone_number" 
                                id="phone_number" 
                                type="tel" 
                                value={formData.phone_number}
                                onChange={handleInputChange}
                                placeholder="e.g., +233 55 123 4567" 
                                required
                            />
                            <small className="form-text text-muted">
                                Applicants will call this number directly to apply
                            </small>
                        </div>
                    </div>
                );
            
            default:
                return null;
        }
    };

    return (
        <>
            <Navbar/>
            <InnerLeadBanner title='Post Opportunity' />

            {/* Payment Retry Modal */}
            {showPaymentRetry && (
                <div className="modal" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Payment Not Completed</h5>
                            </div>
                            <div className="modal-body">
                                <p>Your payment was not completed successfully. Would you like to:</p>
                                <div className="row mt-4">
                                    <div className="col-12 col-sm-6 mb-2 mb-sm-0">
                                        <button 
                                            className="btn btn-secondary w-100" 
                                            onClick={handlePostAsNormal}
                                        >
                                            Post as Normal Ad (Free)
                                        </button>
                                    </div>
                                    <div className="col-12 col-sm-6">
                                        <button 
                                            className="btn btn-primary w-100" 
                                            onClick={handleRetryPayment}
                                        >
                                            Try Payment Again
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Contact section */}
            <section className="contact-section" style={showPaymentRetry ? {filter: 'blur(3px)'} : {}}>
                <div className="container">               
                    <div className="row">
                        <div className="col-12">
                            <h2 className="contact-title">Post Opportunity</h2>
                        </div>
                        <div className="col-lg-8">
                            <form className="form-contact contact_form" onSubmit={handleSubmit} id="opportunityForm">
                                <div className="row">
                                    {/* Image Upload Field */}
                                    <div className="col-12">
                                        <div className="form-group">
                                            <label htmlFor="opportunityImage">Opportunity Image</label>
                                            <input 
                                                type="file" 
                                                className="form-control-file" 
                                                id="opportunityImage" 
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                required
                                            />
                                            <small className="form-text text-muted">
                                                Upload an image that represents this opportunity
                                            </small>
                                        </div>
                                        
                                        {/* Image Preview */}
                                        {imagePreview && (
                                            <div className="image-preview-container mt-3">
                                                <p>Image Preview:</p>
                                                <div className="position-relative d-inline-block">
                                                    <img 
                                                        src={imagePreview} 
                                                        alt="Opportunity preview" 
                                                        className="img-thumbnail" 
                                                        style={{maxHeight: '200px'}}
                                                    />
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-danger btn-sm position-absolute"
                                                        style={{top: '5px', right: '5px'}}
                                                        onClick={removeImage}
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Other form fields */}
                                    <div className="col-sm-6">
                                        <div className="form-group">
                                            <select 
                                                className="form-control" 
                                                name="opportunityType" 
                                                id="opportunityType" 
                                                value={formData.opportunityType}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Type of Opportunity?</option>
                                                <option value="tender">Open Tender</option>
                                                <option value="handyWork">Handy Work</option>
                                                <option value="permanentEmployment">Permanent Employment</option>
                                                <option value="internship">Internship (Paid / Unpaid)</option>
                                                <option value="nationalService">National Service</option>
                                                <option value="competition">Competition & Hackerton</option>
                                                <option value="volunteer">Volunteer Position</option>
                                                <option value="grantAndResearch">Grant & Research Opportunity</option>
                                            </select>
                                        </div>
                                    </div>
                                    
                                    <div className="col-sm-6">
                                        <div className="form-group">
                                            <input 
                                                className="form-control valid" 
                                                name="title" 
                                                id="title" 
                                                type="text" 
                                                value={formData.title}
                                                onChange={handleInputChange}
                                                placeholder="Opportunity Title (30 Max)" 
                                                required
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="col-sm-6">
                                        <div className="form-group">
                                            <select 
                                                className="form-control" 
                                                name="firmOrPersonal" 
                                                id="firmOrPersonal"
                                                value={formData.firmOrPersonal}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Are you posting for a company or yourself?</option>
                                                <option value="company">Company</option>
                                                <option value="self">Self</option>
                                            </select>
                                        </div>
                                    </div>

                                    {formData.firmOrPersonal === 'company' && (
                                        <div className="col-sm-6">
                                            <div className="form-group">
                                                <input 
                                                    className="form-control valid" 
                                                    name="firm" 
                                                    id="firm" 
                                                    type="text" 
                                                    value={formData.firm}
                                                    onChange={handleInputChange}
                                                    placeholder="Name of company" 
                                                    required
                                                />
                                            </div>
                                        </div>
                                    )}
                                    
                                    <div className="col-sm-6">
                                        <div className="form-group">
                                            <input 
                                                className="form-control valid" 
                                                name="remuneration" 
                                                id="remuneration" 
                                                type="text" 
                                                value={formData.remuneration}
                                                onChange={handleInputChange}
                                                placeholder="Remuneration (Could be Confidential)" 
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="col-sm-6">
                                        <div className="form-group">
                                            <select 
                                                className="form-control" 
                                                name="modality" 
                                                id="modality" 
                                                value={formData.modality}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Work modality</option>
                                                <option value="remote">Remote</option>
                                                <option value="onsite">Onsite</option>
                                                <option value="hybrid">Hybrid</option>
                                            </select>
                                        </div>
                                    </div>
                                    
                                    <div className="col-sm-6">
                                        <div className="form-group">
                                            <input 
                                                className="form-control valid" 
                                                name="location" 
                                                id="location" 
                                                type="text" 
                                                value={formData.location}
                                                onChange={handleInputChange}
                                                placeholder="Work location" 
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-sm-6">
                                        <div className="form-group">
                                            <input 
                                                className="form-control valid" 
                                                name="your_email" 
                                                id="your_email" 
                                                type="email" 
                                                value={formData.your_email}
                                                onChange={handleInputChange}
                                                placeholder="Your email address" 
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-sm-6">
                                        <div className="form-group">
                                            <input 
                                                className="form-control valid" 
                                                name="company_phone" 
                                                id="company_phone" 
                                                type="tel" 
                                                value={formData.company_phone}
                                                onChange={handleInputChange}
                                                placeholder="Phone number" 
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-sm-6">
                                        <div className="form-group">
                                            <select 
                                                className="form-control" 
                                                name="application_mode" 
                                                id="application_mode" 
                                                value={formData.application_mode}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Application Mode</option>
                                                <option value="jobflow">Users should apply through Jobflow</option>
                                                <option value="external">Users should apply through an external link</option>
                                                <option value="whatsapp">Users should apply through WhatsApp</option>
                                                <option value="email">Users should apply through Email</option>
                                                <option value="phone_call">Users should apply through Phone call</option>
                                            </select>
                                        </div>
                                    </div>
                                    
                                    {/* WYSIWYG Editor for Opportunity Details */}
                                    <div className="col-12">
                                        <div className="form-group">
                                            <label htmlFor="opportunityDetails" style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                                                Opportunity Details (Including job description, requirements, etc.)
                                            </label>
                                            <JoditEditor
                                                ref={editor}
                                                value={opportunityDetails}
                                                onBlur={handleDetailsChange}
                                                onChange={handleDetailsChange}
                                                tabIndex={1}
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Application Form Section */}
                                    {formData.application_mode && (
                                        <div className="col-12">
                                            <h2 className="contact-title">Application Method</h2>
                                            {renderApplicationForm()}
                                        </div>
                                    )}
                                    
                                    {/* Featured Opportunity Option */}
                                    <div className="col-12 mt-4">
                                        <div className="featured-option-card" style={{
                                            border: '2px solid #ffc107',
                                            borderRadius: '8px',
                                            padding: '20px',
                                            backgroundColor: '#fffdf6',
                                            marginBottom: '20px'
                                        }}>
                                            <h4 style={{ color: '#ffc107', marginBottom: '15px' }}>
                                                <i className="fas fa-crown" style={{ marginRight: '10px' }}></i>
                                                Featured Opportunity
                                            </h4>
                                            
                                            <div className="form-check mb-3">
                                                <input 
                                                    className="form-check-input" 
                                                    type="checkbox" 
                                                    id="is_featured"
                                                    name="is_featured"
                                                    checked={formData.is_featured}
                                                    onChange={handleInputChange}
                                                    style={{ width: '20px', height: '20px' }}
                                                />
                                                <label className="form-check-label" htmlFor="is_featured" style={{ marginLeft: '10px', fontWeight: 'bold', fontSize: '18px' }}>
                                                     Make this a Featured Opportunity (GHS 20 for 24 hours)
                                                </label>
                                            </div>
                                            
                                            <div className="featured-benefits" style={{ marginLeft: '30px' }}>
                                                <h5 style={{ color: '#28a745', marginBottom: '10px' }}>Benefits:</h5>
                                                <ul style={{ paddingLeft: '20px' }}>
                                                    <li>Your opportunity will be sent to all our subscribers via email</li>
                                                    <li>Displayed in a special "Featured Opportunities" section on our homepage</li>
                                                    <li>Increased visibility with a special badge and highlighting</li>
                                                    <li>Up to 3x more applications from qualified candidates</li>
                                                    <li>Priority placement in search results</li>
                                                </ul>
                                                
                                                <div className="payment-info mt-3 p-3" style={{ 
                                                    backgroundColor: '#e7f3ff', 
                                                    borderRadius: '5px',
                                                    borderLeft: '4px solid #007bff'
                                                }}>
                                                    <h6 style={{ color: '#007bff' }}>Payment Process:</h6>
                                                    <p className="mb-1">If you select this option, you'll be redirected to a secure Paystack payment page after submitting the form.</p>
                                                    <p className="mb-0"><small>Your opportunity will be featured once payment is confirmed.</small></p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="form-group mt-3">
                                    <button 
                                        type="submit" 
                                        className="button button-contactForm boxed-btn"
                                        disabled={isSubmitting || paymentProcessing}
                                    >
                                        {paymentProcessing ? (
                                            <>
                                                <span 
                                                    className="spinner-border spinner-border-sm" 
                                                    role="status" 
                                                    aria-hidden="true"
                                                    style={{marginRight: '8px'}}
                                                ></span>
                                                Redirecting to Payment...
                                            </>
                                        ) : isSubmitting ? (
                                            <>
                                                <span 
                                                    className="spinner-border spinner-border-sm" 
                                                    role="status" 
                                                    aria-hidden="true"
                                                    style={{marginRight: '8px'}}
                                                ></span>
                                                Posting...
                                            </>
                                        ) : (
                                            'Finish'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                        <div className="col-lg-3 offset-lg-1">
                            <div className="media contact-info">
                                <span className="contact-info__icon"><i className="ti-home"></i></span>
                                <div className="media-body">
                                    <h3>Do you need any assistance?</h3>
                                    <p>Reach us </p>
                                </div>
                            </div>
                            <div className="media contact-info">
                                <span className="contact-info__icon"><i className="ti-tablet"></i></span>
                                <div className="media-body">
                                    <h3>+233 559 801 548</h3>
                                    <p>Mon to Sun 8am to 6pm</p>
                                </div>
                            </div>
                            <div className="media contact-info">
                                <span className="contact-info__icon"><i className="ti-email"></i></span>
                                <div className="media-body">
                                    <h3>support@jobflowonline.com</h3>
                                    <p>Send us your query anytime!</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            
            <style jsx>{`
                .image-preview-container {
                    padding: 15px;
                    border: 1px dashed #ddd;
                    border-radius: 5px;
                    background-color: #f9f9f9;
                }
                
                .button:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }
                
                .modal {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    z-index: 1050;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
            `}</style>
        </>
    )
}

export default PostOpportunity